import type { AppDispatch } from '../../data/store';
import type { WebSocketSessionInfo } from './webSocketService';
import * as tripLegSlice from '../../data/tripleg.slice';
import { tripLegSlice as tripLegAdapterSlice } from '../../data/slices/trip-leg.slice';
import { completePendingDiscussionClearAsync } from '../../data/slices/discussion.slice';
import * as passengerSlice from '../../data/passenger.slice';
import * as providerSlice from '../../data/provider.slice';
import { TripLegEventType } from '../../data/events/trip-leg';
import { vehicleSlice } from '@/data/slices/vehicle.slice';
import { driverSlice } from '@/data/slices/driver.slice';
import type { Driver } from '@/data/models/driver.model';

/**
 * Some events (e.g. TripLegDriverAssigned, see EventTripLegDriverAssigned)
 * send a lean driver with firstName/lastName instead of the Driver model's
 * driverFirstName/driverLastName. Map those so the driver entity slice only
 * ever holds Driver-shaped data.
 */
function normalizeEventDriver(
  driver: Partial<Driver> & { firstName?: string; lastName?: string },
): Driver {
  const { firstName, lastName, ...rest } = driver;
  return {
    ...rest,
    ...(rest.driverFirstName == null &&
      firstName != null && { driverFirstName: firstName }),
    ...(rest.driverLastName == null &&
      lastName != null && { driverLastName: lastName }),
  } as Driver;
}

/**
 * Method for handling all TripLeg Events
 * @param sessionInfo The webSocketService sessionInfo object. Contains data copied over from the Redux store as needed
 * @param dispatch The Redux store dispatch function
 * @param detailType A string representing the type of event. Must be one of the TripLegEventType enum values
 * @param detail An object containing the event data, in this case a TripLeg
 */
function updateTripLeg(
  sessionInfo: WebSocketSessionInfo,
  dispatch: AppDispatch,
  detailType: TripLegEventType,
  // biome-ignore lint/suspicious/noExplicitAny: Events are not following a unified type
  detail: any,
) {
  // Entity Adapter supported slice updates
  // A drafted or opened event only updates a trip leg that's already loaded;
  // a newly created one is left for a page's fetch to load in full (see
  // tripLegOpened in trip-leg.slice.ts). Some events are special cases that need
  // specific logic, so we divert them to their handlers. The default case is
  // for updating the trip leg from all other events.
  dispatch(
    tripLegAdapterSlice.actions.addToTripLegEventQueue({ detailType, detail }),
  );
  // Upsert the driver and vehicle before the trip leg so selectors merging
  // from them see the latest data.
  if (detail.driver) {
    dispatch(
      driverSlice.actions.upsertDriver(normalizeEventDriver(detail.driver)),
    );
  }
  if (detail.vehicle) {
    dispatch(vehicleSlice.actions.upsertVehicle(detail.vehicle));
  }
  switch (detailType) {
    case TripLegEventType.TripLegDrafted:
    case TripLegEventType.TripLegOpened:
      dispatch(tripLegAdapterSlice.actions.tripLegOpened(detail));
      break;
    case TripLegEventType.TripLegDriverAssigned:
      dispatch(tripLegAdapterSlice.actions.tripLegDriverAssigned(detail));
      break;
    case TripLegEventType.TripLegExceptionCleared:
      dispatch(tripLegAdapterSlice.actions.tripLegExceptionCleared(detail));
      break;
    case TripLegEventType.TripLegEscalated:
      dispatch(tripLegAdapterSlice.actions.tripLegEscalated(detail));
      break;
    case TripLegEventType.WillCallTripLegActivated:
      dispatch(tripLegAdapterSlice.actions.tripLegWillCallActivated(detail));
      break;
    default:
      dispatch(tripLegAdapterSlice.actions.tripLegUpdated(detail));
  }

  // TODO: Remove the below switch case once all components that use the old
  // trip leg slice have been migrated to the entity adapter supported slice.
  switch (detailType) {
    case TripLegEventType.TripLegDrafted:
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      break;
    case TripLegEventType.TripLegOpened:
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      dispatch(
        providerSlice.completePendingReassignTripLegAsync(
          detail.tripLegReferenceID,
        ),
      );
      break;
    case TripLegEventType.TripLegException:
    case TripLegEventType.TripLegExceptionAssigned:
    case TripLegEventType.TripLegExceptionCleared:
    case TripLegEventType.TripLegEscalationCleared:
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      break;
    case TripLegEventType.TripLegInventoried:
    case TripLegEventType.DraftTripLegUpdated:
    case TripLegEventType.OpenTripLegUpdated:
    case TripLegEventType.TripLegOffered:
    case TripLegEventType.OfferedTripLegUpdated:
    case TripLegEventType.TripLegManuallyAllocated:
    case TripLegEventType.TripLegAccepted:
    case TripLegEventType.AcceptedTripLegUpdated:
    case TripLegEventType.TripLegVehicleAssigned:
    case TripLegEventType.TripLegConfirmed:
    case TripLegEventType.TripLegDriverInTransit:
    case TripLegEventType.TripLegBoarded:
    case TripLegEventType.TripLegArrived:
    case TripLegEventType.TripLegCompleted:
    case TripLegEventType.TripLegRejected:
    case TripLegEventType.TripLegCanceled:
      dispatch(
        passengerSlice.updateTripLegInReservation({
          reservationReferenceID: detail.reservationReferenceID,
          tripLegReferenceID: detail.tripLegReferenceID,
          transportationProviderID: detail.transportationProviderID,
          status: detail.status,
        }),
      );
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      break;
    case TripLegEventType.TripLegCanceledLate:
      dispatch(
        passengerSlice.updateTripLegInReservation({
          reservationReferenceID: detail.reservationReferenceID,
          tripLegReferenceID: detail.tripLegReferenceID,
          transportationProviderID: detail.transportationProviderID,
          status: detail.status,
        }),
      );
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      dispatch(completePendingDiscussionClearAsync(detail.tripLegReferenceID));
      break;
    case TripLegEventType.TripLegNotLoaded:
    case TripLegEventType.TripLegAbandoned:
    case TripLegEventType.TripLegDenied:
    case TripLegEventType.TripLegHeld:
    case TripLegEventType.OnHoldTripLegUpdated:
    case TripLegEventType.TripLegUnderDiscussion:
    case TripLegEventType.TripLegUnderDiscussionCleared:
    case TripLegEventType.WillCallTripLegActivated:
      dispatch(
        passengerSlice.updateTripLegInReservation({
          reservationReferenceID: detail.reservationReferenceID,
          tripLegReferenceID: detail.tripLegReferenceID,
          transportationProviderID: detail.transportationProviderID,
          status: detail.status,
        }),
      );
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      break;
    case TripLegEventType.TripLegDriverAssigned:
      dispatch(tripLegSlice.upsertAllTripLegsDriverAssigned(detail));
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      break;
    case TripLegEventType.TripLegEscalated:
      dispatch(tripLegSlice.handleTripLegEscalated(detail.tripLegReferenceID));
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      break;
    case TripLegEventType.TripLegCountsUpdated:
      dispatch(tripLegSlice.setTripLegCounts(detail));
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      break;
  }
}

// Deliberately NOT re-exporting TripLegEventType: importing the enum through
// this handler pulls consumers into the handler's dependency graph (a latent
// import cycle with the data layer) — import it from '@/data/events/trip-leg'.
export { updateTripLeg };
